/**
 * "How should we bring it in?" — the first question every importer asks.
 *
 * Four apps had written this identically: a label, a segmented control, and the description of
 * whichever mode is selected. office-to-design's and cad-to-design's copies were
 * byte-for-byte the same. The modes differ between plugins and the shape never does, so the
 * shape belongs here and the modes stay with the app that knows them.
 *
 * THE DESCRIPTION IS PART OF THE CONTROL, not a sibling of it. It answers the question the
 * segmented control raises — a designer reading "Vector" wants to know what that costs them —
 * and keeping them together is what stops one app showing it above the control, another below,
 * and a third not at all.
 */

import { Box, Text } from '@chakra-ui/react';
import { SectionLabel } from './SectionLabel';
import { SegmentedControl } from './SegmentedControl';

export interface ImportModeOption<T extends string> {
  value: T;
  /** The word on the segment — "Editable", "Vector", "Image". */
  label: string;
  /** One line on what this mode actually produces. Shown only while it is selected. */
  description?: string;
}

export interface ModeSectionProps<T extends string> {
  modes: ImportModeOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Overridable for an importer whose question is not about a file — rarely needed. */
  label?: string;
}

export function ModeSection<T extends string>({
  modes,
  value,
  onChange,
  label = 'How should we bring it in?',
}: ModeSectionProps<T>) {
  const description = modes.find((mode) => mode.value === value)?.description;

  return (
    <Box>
      <SectionLabel>{label}</SectionLabel>
      <SegmentedControl
        value={value}
        onChange={onChange}
        options={modes.map((mode) => ({ value: mode.value, label: mode.label }))}
      />
      {description && (
        <Text mt="1.5" fontSize="2xs" color="figma.textMuted" lineHeight="1.45">
          {description}
        </Text>
      )}
    </Box>
  );
}
